import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmployeeDto, WorkOrderDto } from 'src/api';
import { MatButtonModule } from '@angular/material/button';
import { WorkOrdersRepository } from '../workorders.repository';
import { EmployeesRepository } from 'src/employees/employees.repository';
import { EmployeesQuery } from 'src/employees/state/employees.query';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import { MatIconModule } from '@angular/material/icon';
import { DrawerWorkorderTitleComponent } from '../drawers/drawer-workorder-title/drawer-workorder-title.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { SelectableEmployee } from './selectable-employee';
import { EmployeeVisitsQuery } from 'src/employee-visits/state/employeeVisits.query';
import { EmployeeVisitsRepository } from 'src/employee-visits/employeeVisits.repository';
import { DrawerWorkorderActionsComponent } from '../drawers/drawer-workorder-actions/drawer-workorder-actions.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-workorder-assign-specialists',
  standalone: true,
  templateUrl: './workorder-assign-specialists.component.html',
  styleUrls: ['./workorder-assign-specialists.component.scss'],
  imports: [
    CommonModule,
    DrawerWorkorderTitleComponent,
    MatButtonModule,
    MatIconModule,
    DrawerWorkorderActionsComponent,
    TranslateModule,
  ],
})
export class WorkorderAssignSpecialistsComponent implements OnInit {
  availableEmployees$: Observable<EmployeeDto[]>;
  assignedEmployees$: Observable<SelectableEmployee[]>;

  selectableEmployees: BehaviorSubject<SelectableEmployee[]> = new BehaviorSubject<SelectableEmployee[]>([]);
  selectableEmployees$: Observable<SelectableEmployee[]> = this.selectableEmployees.asObservable();

  searchTerm: BehaviorSubject<string> = new BehaviorSubject<string>('');
  searchTerm$: Observable<string> = this.searchTerm.asObservable();

  get workOrder(): WorkOrderDto {
    return this.data.workOrder;
  }

  constructor(
    public drawerRef: MtxDrawerRef<WorkorderAssignSpecialistsComponent>,
    @Inject(MTX_DRAWER_DATA) public data: any,
    private employeesRepository: EmployeesRepository,
    private employeesQuery: EmployeesQuery,
    private workOrdersRepository: WorkOrdersRepository,
    private employeeVisitsRepository: EmployeeVisitsRepository,
    private employeeVisitsQuery: EmployeeVisitsQuery
  ) {
    combineLatest([
      this.employeesQuery.selectAll(),
      this.employeeVisitsQuery.selectAll({
        filterBy: (employeeVisit) => employeeVisit.workOrderId == this.workOrder.id,
      }),
    ])
      .pipe(
        takeUntilDestroyed(),
        map(([employees, employeeVisits]) => {
          var selectableEmployees: SelectableEmployee[] = employees.map((employee) => ({
            employee,
            selected: (this.workOrder.specialistIds &&
              this.workOrder.specialistIds.includes(employee.id as number)) as boolean,
            visitCount: employeeVisits.filter((ev) => ev.employeeId == employee.id).length,
          }));

          this.selectableEmployees.next(selectableEmployees);
        })
      )
      .subscribe();

    this.availableEmployees$ = combineLatest([this.selectableEmployees$, this.searchTerm$]).pipe(
      map(([selectable, searchTerm]) => {
        var employees = selectable
          .filter((x: SelectableEmployee) => !x.selected)
          .map((x: SelectableEmployee) => x.employee);

        if (searchTerm) {
          employees = employees.filter(
            (e) =>
              e.name?.toLowerCase().includes(searchTerm.toLocaleLowerCase()) ||
              e.nationalId?.toLowerCase().includes(searchTerm.toLocaleLowerCase())
          );
        }

        return employees;
      })
    );

    this.assignedEmployees$ = this.selectableEmployees$.pipe(
      map((x) => {
        var employees = x.filter((x) => x.selected);
        return employees;
      })
    );
  }

  ngOnInit(): void {
    this.employeesRepository.loadEmployees();
    this.employeeVisitsRepository.loadWorkOrderEmployeeVisits(this.workOrder.id as number);
  }

  toggleEmployeeSelected(employeeToToggle: EmployeeDto, visitCount: number = 0) {
    if (!visitCount) {
      this.selectableEmployees.next(
        this.selectableEmployees.value.map((selectable: SelectableEmployee) =>
          selectable.employee.id === employeeToToggle.id
            ? { ...selectable, selected: !selectable.selected }
            : selectable
        )
      );
    }
  }

  updateSearchTerm(searchTerm: string) {
    this.searchTerm.next(searchTerm);
  }

  save() {
    const specialistIds = this.selectableEmployees.value.filter((x) => x.selected).map((x) => x.employee.id);
    this.workOrdersRepository.assignSpecialists(this.workOrder.id as number, specialistIds as number[]);
    this.drawerRef.dismiss();
  }

  close() {
    this.drawerRef.dismiss();
  }
}
